import { useCurrentUser } from '@centecomm/portal-service-react';
import { LDContext, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';

import useSettingsFromState from '../../../State/Settings/Hooks/useSettingsFromState';
import { LaunchDarklyFlags } from './launchDarklyFlags';

const useLaunchDarkly = (flag: LaunchDarklyFlags, noLoadWait = false): any => {
    const ldClient = useLDClient();

    const { currentUser } = useCurrentUser();
    const { isLoaded: settingsLoaded, settings } = useSettingsFromState();

    const [flagValue, setFlagValue] = useState<any | undefined>(undefined);
    const [isIdentified, setIsIdentified] = useState(false);

    useEffect(() => {
        if (!ldClient || !settingsLoaded) return;

        const usercompanyIds = Array.from(new Set(currentUser?.tenant?.occupants?.map((o) => o.companyId) || []));

        // load from Settings (as OO), otherwise pickup the first companyId from the currentUser's
        // tenant (user/current api call)
        const companyId = settings?.companyId || (usercompanyIds.length > 0 ? usercompanyIds[0] : '');

        // We can expland on this to "kind: multi", passing in multiple contexts to target for
        // different flags or features per org/tenant/property
        /**
     * e.g.
     *    ldClient.identify({
                kind: 'multi',
                //Per discussion with Axel Joe and a few others, we are not going to use user context.
                //We will stick with broader context like Company below.
                user: {
                    key: currentUser?.email,
                    userType: currentUser?.userType,
                    firstName: currentUser?.firstName,
                    lastName: currentUser?.lastName,
                },
                company: {
                    key: '2',
                    companyName: 'Test Company2',
                    currentProperty: 'Test Property2',
                    currentOccupant: 'Test Occupant2',
                    userType: currentUser?.userType,
                    firstName: currentUser?.firstName,
                    lastName: currentUser?.lastName,
                }
            });
     */
        const context: LDContext = {
            kind: 'multi',
            user: {
                key: currentUser?.email,
                userType: currentUser?.userType,
                firstName: currentUser?.firstName,
                lastName: currentUser?.lastName,
            },
            company: {
                key: companyId.toString() || 'unknown-company',
            },
        };

        ldClient.identify(context).then(() => {
            setIsIdentified(true);
        });
        // `settings?.companyId` rather than `settings`: the whole settings object takes a new identity on every
        // SET_SETTINGS dispatch, so any occupant or feature setting being saved re-identified the LaunchDarkly context
        // and re-fetched every flag. The company id is the only thing read from it here.
    }, [ldClient, currentUser, settingsLoaded, settings?.companyId]);

    // If noLoadWait is true, we return the flag value directly without waiting for identification
    if (noLoadWait) {
        return ldClient?.variation(flag);
    }

    // To consider for future, could be used to set the flag value in the state, but we are not using it for now.
    useMemo(() => {
        if (!ldClient || !isIdentified) return;

        // value could technically set it as a boolean, but it could be a string or number or json in the future
        const flagValueFromLD = ldClient?.variation(flag);
        setFlagValue(flagValueFromLD || undefined);
    }, [ldClient, currentUser, isIdentified, flag]);

    return flagValue;
};

export default useLaunchDarkly;
